import type { TokenFetch, TokenResult } from "@bindery/auth-consumer/browser";
import type { ReaderName } from "@bindery/shared/contracts/session";
import {
  SESSION_PATH,
  SESSION_REFUSALS,
  sessionAnswerSchema,
} from "../contracts/session";

export type SessionState =
  | { status: "loading" }
  | { status: "signed-out" }
  /** A child's reader session: this tool needs an adult to sign in. */
  | { status: "forbidden" }
  | { status: "unreachable" }
  | {
      status: "signed-in";
      boundReader: ReaderName | null;
      readers: readonly ReaderName[];
    };

export type SettledSessionState = Exclude<SessionState, { status: "loading" }>;

export interface SessionDeps {
  fetchTokenResult: () => Promise<TokenResult>;
  fetcher: TokenFetch;
}

export async function fetchSession(
  deps: SessionDeps,
  signal?: AbortSignal,
): Promise<SettledSessionState> {
  const token = await deps.fetchTokenResult();
  if (token.kind !== "token") return { status: token.kind };
  let response: Response;
  try {
    response = await deps.fetcher(SESSION_PATH, {
      headers: { authorization: `Bearer ${token.token}` },
      credentials: "same-origin",
      ...(signal === undefined ? {} : { signal }),
    });
  } catch {
    return { status: "unreachable" };
  }
  if (response.status === SESSION_REFUSALS.unauthenticated) {
    return { status: "signed-out" };
  }
  if (response.status === SESSION_REFUSALS.forbidden) {
    return { status: "forbidden" };
  }
  if (!response.ok) return { status: "unreachable" };
  const answer = sessionAnswerSchema.safeParse(await bodyOf(response));
  if (!answer.success) return { status: "unreachable" };
  return { status: "signed-in", ...answer.data };
}

async function bodyOf(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
